import { createUseStyles } from 'react-jss';
import color from 'color';

import { ThemeProps } from '@styles/theme';

import {
    FONT_SECONDARY, RADIUS, SPACING_BASE,
} from '@styles/constants';
import { customScroll, flex } from '@resources/mixins';

export const getCustomStyles = (themeColors: ThemeProps['colors']) => ({
    menu: (provided: object) => ({
        ...provided,
        boxShadow: 'none',
        marginTop: 1,
        border: `1.5px solid ${themeColors.violet_primary}`,
        backgroundColor: themeColors.white,

        '& > div': {
            maxHeight: 400,
            ...customScroll(),
        },
    }),
    singleValue: (provided: object) => ({
        ...provided,
        fontSize: 16,
        color: themeColors.black,
    }),
    multiValue: (provided: object) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
        borderRadius: RADIUS,
        minHeight: 32,
        margin: SPACING_BASE,
        backgroundColor: color(themeColors.violet_primary).fade(0.92).string(),
        maxWidth: 'calc(100% - 10px)',
    }),
    multiValueLabel: (provided: object) => ({
        ...provided,
        fontFamily: FONT_SECONDARY,
        lineHeight: 1.25,
        color: themeColors.violet_primary,
        fontSize: 16,
    }),
    indicatorsContainer: (provided: object) => ({
        ...provided,
        alignItems: 'center',
    }),
    dropdownIndicator: (provided: object, state: Record<string, any>) => ({
        ...provided,
        color: themeColors.violet_primary,
        transition: 'transform .2s ease',
        transformOrigin: 'center',
        transform: state.selectProps.menuIsOpen ? 'scale(1, -1)' : 'scale(1, 1)',
    }),
    indicatorSeparator: (provided: object) => ({
        ...provided,
        display: 'none',
    }),
    control: (provided: object, state: Record<string, any>) => {
        const { error } = state.selectProps;

        return ({
            ...provided,
            borderWidth: 1.5,
            borderColor: error
                ? themeColors.red
                : state.isFocused
                    ? themeColors.violet_primary
                    : themeColors.grey_light,
            backgroundColor: themeColors.white,
            boxShadow: 'none',
            maxHeight: 150,
            minHeight: 44,
            paddingTop: 1,
            paddingBottom: 1,
            ...customScroll(),

            '&:hover': {
                borderColor: `${color(themeColors.violet_primary).fade(0.62)}`,
            },
        })
    },
    option: (provided: object, state: Record<string, any>) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? color(themeColors.violet_primary).fade(0.85).string()
            : state.isFocused
                ? color(themeColors.violet_primary).fade(0.92).string()
                : themeColors.white,
        color: themeColors.black,
    }),
    input: (provided: object) => ({
        ...provided,
        color: themeColors.black,
    }),
});

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    select: {
        '&:focus': {
            outline: 'none',
        },
    },
    wrapper: {
        position: 'relative',
    },
    labelWithError: {
        color: theme.colors.red,
    },
    errorMessage: {
        color: theme.colors.red,
        fontSize: 14,
        position: 'absolute',
        width: '100%',
        left: 0,
        bottom: -20,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    valueRemove: {
        height: '100%',
        color: theme.colors.violet_primary,
        marginLeft: theme.spacing(0.5),
        padding: theme.spacing(0.5),
        ...flex('row', 'center', 'center'),

        '&:hover': {
            cursor: 'pointer',
            backgroundColor: 'inherit',
        },
    },
    valueRemoveIcon: {
        fontSize: 'inherit !important',
    },
}));
